import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isLawyer, logout, refreshToken } from './utils/session';
import axios from 'axios';
import { sendEvent } from './utils/requests/user_events';
import { requestPushPermissionAndGetToken } from './utils/pushNotifications';
import { registerPushToken } from './utils/requests/notifications';

const fetchData = async (forceRefresh: boolean) => {
  if (!localStorage.getItem('jwt')) {return}
  
  const now = Date.now();
  const lastRun = Number(localStorage.getItem('last_check_ts') ?? 0);
  if (now - lastRun < 5000) {return} // no ejecutar si pasaron menos de 5 segundos

  localStorage.setItem('last_check_ts', now.toString());

  try {
    await checkUserByID(forceRefresh);
    await checkGetNotifications(forceRefresh);
    await checkGetStickyNotifications(forceRefresh);
    await checkContractedServices(forceRefresh);
    await checkMarketplaceFilters(forceRefresh);
    checkLawyerQuotaUsage(forceRefresh);
    checkSendDailyEvent();
    checkSendLastActive(forceRefresh);
    checkDownloadOnboardings(forceRefresh);
    checkPushNotificationPermission();
  } catch (error) {
    localStorage.removeItem('last_check_ts');
    console.error("Error in fetchData:", error);
    return
  }

  localStorage.removeItem('last_check_ts');
};

const checkUserByID = async (refresh: boolean) => {
  try {
    if (!localStorage.getItem('jwt')) {
      return;
    }
    const lastUpdate = localStorage.getItem('last_check_user');
    const now = new Date();
    const nowTimestamp = now.getTime();
    
    const lastUpdateTimestamp = lastUpdate ? parseInt(lastUpdate, 10) : 0;
    const interval = 1 * 60 * 60 * 1000; // 1 hs

    if (nowTimestamp - lastUpdateTimestamp >= interval || refresh) {
      console.log("Obtaining user info");
      const success = await getUserByID(0);
      if (success) {
          localStorage.setItem('last_check_user', nowTimestamp.toString());
      }
    }
  } catch (error) {
    console.error('Error al actualizar la información:', error);
  }
};

async function getUserByID(count: number): Promise<boolean> {
  try {
      const jwt = localStorage.getItem('jwt');
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : {};

      const res = await axios.get(`https://api.abogalia.com.ar/abogalia/api/v1/users/${user?.id}`,
      {
          headers: {
              Authorization: `Bearer ${jwt}`,
              Accept: 'application/json',
          },
      }
      );

      if (res.status === 200) {
        localStorage.setItem("user", JSON.stringify(res.data));
        return true;
      }
      return false;
  } catch (error: any) {
      if (error.response && error.response.status === 401 && count === 0) {
        console.log("Unauthorized Obtaining user info: ", error);
        const shouldRetry = await refreshToken(null);
        if (shouldRetry) {
          await getUserByID(1);
        } else {
          logout(null);
        }
      } else if (error.response && error.response.status === 404) {
        const errorData = error.response.data;
        if (errorData?.keys && errorData.keys[0] === 'user_not_found') {
          console.log("User not found, logging out: ", error);
          logout(null);
        }
      }
      
      console.log("Error Obtaining user info: ", error);
      return false;
  }
}

const checkLawyerQuotaUsage = async (forceRefresh: boolean) => {
  try {
    const storedUser = localStorage.getItem('user');
    const user = storedUser ? JSON.parse(storedUser) : {};
    if (!isLawyer(user)) return;

    const lastCheck = localStorage.getItem('last_check_lawyer_quota_usage');
    const now = new Date();
    const nowTimestamp = now.getTime();
    
    const lastUpdateTimestamp = lastCheck ? parseInt(lastCheck, 10) : 0;
    const interval = 2 * 60 * 60 * 1000; // 2 hs

    if (nowTimestamp - lastUpdateTimestamp >= interval || forceRefresh) {
      console.log("Checking lawyer quota usage");
      const success = await getLawyerQuotaUsage(0);
      if (success) {
          localStorage.setItem('last_check_lawyer_quota_usage', nowTimestamp.toString());
      }
    }
  } catch (error) {
    console.error('Error getting count action:', error);
  }
};

async function getLawyerQuotaUsage(count: number): Promise<boolean> {
  try {
      const jwt = localStorage.getItem('jwt');

      const res = await axios.get(`https://api.abogalia.com.ar/abogalia/api/v1/users/lawyer/quota/usage`,
        {
          headers: {
            Authorization: `Bearer ${jwt}`,
            Accept: 'application/json',
          },
        }
      );

      if (res.status === 200) {
        localStorage.setItem("lawyer_quota_usage", JSON.stringify(res.data));
        return true;
      }
      return false;
  } catch (error: any) {
      if (error.response && error.response.status === 401 && count === 0) {
        console.log("Unauthorized Checking lawyer quota usage: ", error);
        const shouldRetry = await refreshToken(null);
        if (shouldRetry) {
            await getLawyerQuotaUsage(1);
        }
      }
      
      console.log("Error Checking lawyer quota usage: ", error);
      return false;
  }
}

const parseBoolean = (value: any) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  if (typeof value === 'number') return value === 1;
  return false;
};

const normalizeMarketplaceFilters = (rawFilters: any[]) => {
  return rawFilters.map((filter: any, index: number) => {
    const numericId = Number(filter.id);
    const filterItems = Array.isArray(filter.filters) ? filter.filters : [];

    const getValue = (key: string) => {
      const found = filterItems.find((item: any) => item.key === key);
      return found?.value;
    };

    return {
      id: Number.isFinite(numericId) ? numericId : Date.now() + index,
      name: filter.name ?? '',
      isDefault: Boolean(filter.is_default),
      filters: {
        searchTerm: getValue('search_term') ?? '',
        selectedCategory: getValue('category') ?? '',
        selectedLocation: getValue('location') ?? '',
        withoutOffers: parseBoolean(getValue('without_offers')),
        selectedCanPay: getValue('can_pay') ?? '',
        selectedLeadQuality: getValue('lead_quality') ?? '',
      },
    };
  });
};

const checkMarketplaceFilters = async (forceRefresh: boolean) => {
  try {
    const storedUser = localStorage.getItem('user');
    const user = storedUser ? JSON.parse(storedUser) : {};
    if (!isLawyer(user)) return;

    const lastUpdate = localStorage.getItem('last_check_marketplace_filters');
    const now = new Date();
    const nowTimestamp = now.getTime();

    const lastUpdateTimestamp = lastUpdate ? parseInt(lastUpdate, 10) : 0;
    const interval = 24 * 60 * 60 * 1000; // 24 hs

    if (nowTimestamp - lastUpdateTimestamp >= interval || forceRefresh) {
      console.log("Checking marketplace filters");
      const success = await getMarketplaceFilters(0);
      if (success) {
        localStorage.setItem('last_check_marketplace_filters', nowTimestamp.toString());
      }
    }
  } catch (error) {
    console.error('Error al sincronizar filtros de marketplace:', error);
  }
};

async function getMarketplaceFilters(count: number): Promise<boolean> {
  try {
    const jwt = localStorage.getItem('jwt');
    if (!jwt) {
      return false;
    }

    const res = await axios.get(`https://api.abogalia.com.ar/abogalia/api/v1/users/marketplace/filters`, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        Accept: 'application/json',
      },
    });

    if (res.status === 200 && Array.isArray(res.data)) {
      const normalizedFilters = normalizeMarketplaceFilters(res.data);
      localStorage.setItem("marketplace_saved_filters", JSON.stringify(normalizedFilters));
      return true;
    }
    return false;
  } catch (error: any) {
    if (error.response && error.response.status === 401 && count === 0) {
      console.log("Unauthorized Obtaining marketplace filters: ", error);
      const shouldRetry = await refreshToken(null);
      if (shouldRetry) {
        await getMarketplaceFilters(1);
      }
    }

    console.log("Error Obtaining marketplace filters: ", error);
    return false;
  }
}

const checkGetNotifications = async (forceRefresh: boolean) => {
  try {
    const lastUpdate = localStorage.getItem('last_check_get_notifications');
    const now = new Date();
    const nowTimestamp = now.getTime();
    
    const lastUpdateTimestamp = lastUpdate ? parseInt(lastUpdate, 10) : 0;
    const interval = 1 * 60 * 60 * 1000; // 1 hs

    if (nowTimestamp - lastUpdateTimestamp >= interval || forceRefresh) {
      console.log("Obtaining user notifications");
      const success = await getNotifications(0);
      if (success) {
          localStorage.setItem('last_check_get_notifications', nowTimestamp.toString());
      }
    }
  } catch (error) {
    console.error('Error al obtener las notificaciones:', error);
  }
};

async function getNotifications(count: number): Promise<boolean> {
  try {
      const jwt = localStorage.getItem('jwt');
      const res = await axios.get(`https://api.abogalia.com.ar/abogalia/api/v1/users/notifications`, {
        headers: {
            Authorization: `Bearer ${jwt}`,
            Accept: 'application/json',
        },
        params: {
          is_sticky: false,
        },
      });

      if (res.status === 200) {
          const results = res.data.results ?? [];
          const abogaliaNotifications = results.filter((n: { service?: string }) => n.service === 'abogalia');
          const legalbotNotifications = results.filter((n: { service?: string }) => n.service === 'legalbot');
          localStorage.setItem("user_notifications", JSON.stringify(abogaliaNotifications));
          localStorage.setItem("user_legalbot_notifications", JSON.stringify(legalbotNotifications));
          return true;
      }
      return false;
  } catch (error: any) {
    console.log("Unauthorized Obtaining user notifications: ", error);
    if (error.response && error.response.status === 401 && count === 0) {
        const shouldRetry = await refreshToken(null);
        if (shouldRetry) {
            await getNotifications(1);
        }
    }
    
    console.log("Error Obtaining user notifications: ", error);
    return false;
  }
}

const checkGetStickyNotifications = async (forceRefresh: boolean) => {
  try {
    const lastUpdate = localStorage.getItem('last_check_get_sticky_notifications');
    const now = new Date();
    const nowTimestamp = now.getTime();
    
    const lastUpdateTimestamp = lastUpdate ? parseInt(lastUpdate, 10) : 0;
    const interval = 24 * 60 * 60 * 1000; // 24 hs

    if (nowTimestamp - lastUpdateTimestamp >= interval || forceRefresh) {
      console.log("Obtaining user sticky notifications");
      const success = await getStickyNotifications(0);
      if (success) {
          localStorage.setItem('last_check_get_sticky_notifications', nowTimestamp.toString());
      }
    }
  } catch (error) {
    console.error('Error al obtener las sticky notifications:', error);
  }
};

async function getStickyNotifications(count: number): Promise<boolean> {
  try {
      const jwt = localStorage.getItem('jwt');
      const res = await axios.get(`https://api.abogalia.com.ar/abogalia/api/v1/users/notifications`, {
        headers: {
          Authorization: `Bearer ${jwt}`,
          Accept: 'application/json',
        },
        params: {
          is_sticky: true,
          is_read: false,
          is_expired: false,
          page: 1,
          limit: 1,
        },
      });

      if (res.status === 200) {
          const results = res.data.results ?? [];
          const abogaliaNotifications = results.filter((n: { service?: string }) => n.service === 'abogalia');
          const legalbotNotifications = results.filter((n: { service?: string }) => n.service === 'legalbot');
          localStorage.setItem("user_sticky_notifications", JSON.stringify(abogaliaNotifications));
          localStorage.setItem("user_legalbot_sticky_notifications", JSON.stringify(legalbotNotifications));
          return true;
      }
      return false;
  } catch (error: any) {
    if (error.response && error.response.status === 401 && count === 0) {
        console.log("Unauthorized Obtaining user sticky notifications: ", error);
        const shouldRetry = await refreshToken(null);
        if (shouldRetry) {
            await getStickyNotifications(1);
        }
    }
    console.log("Error Obtaining user sticky notifications: ", error);
    return false;
  }
}

const checkContractedServices = async (forceRefresh: boolean) => {
  try {
    const lastUpdate = localStorage.getItem('last_check_contracted_services');
    const now = new Date();
    const nowTimestamp = now.getTime();
    
    const lastUpdateTimestamp = lastUpdate ? parseInt(lastUpdate, 10) : 0;
    const interval = 2 * 60 * 60 * 1000; // 2 hs

    if (nowTimestamp - lastUpdateTimestamp >= interval || forceRefresh) {
      console.log("Obtaining user contracted services");
      const success = await getContractedServices(0);
      if (success) {
          localStorage.setItem('last_check_contracted_services', nowTimestamp.toString());
      }
    }
  } catch (error) {
    console.error('Error al obtener los servicios contratados:', error);
  }
};

async function getContractedServices(count: number): Promise<boolean> {
  try {
      const jwt = localStorage.getItem('jwt');
      const res = await axios.get(`https://api.abogalia.com.ar/abogalia/api/v1/users/contracted-services`, {
        headers: {
          Authorization: `Bearer ${jwt}`,
          Accept: 'application/json',
        },
      });

      if (res.status === 200) {
        localStorage.setItem("user_contracted_services", JSON.stringify(res.data));
        return true;
      }
      return false;
  } catch (error: any) {
    if (error.response && error.response.status === 401 && count === 0) {
        console.log("Unauthorized Obtaining user contracted services: ", error);
        const shouldRetry = await refreshToken(null);
        if (shouldRetry) {
            await getContractedServices(1);
        }
    }
    
    console.log("Error Obtaining user contracted services: ", error);
    return false;
  }
}

const checkSendDailyEvent = async () => {
  try {
    const lastSend = localStorage.getItem('last_send_daily_event');
    const now = new Date();
    const nowTimestamp = now.getTime();
    
    const lastUpdateTimestamp = lastSend ? parseInt(lastSend, 10) : 0;
    const interval = 24 * 60 * 60 * 1000; // 24 hs

    if (nowTimestamp - lastUpdateTimestamp >= interval) {
      console.log("Sending daily event");
      const success = await sendDailyEvent("daily_checkin", 0);
      if (success) {
          localStorage.setItem('last_send_daily_event', nowTimestamp.toString());
      }
    }
  } catch (error) {
    console.error('Error al enviar daily event:', error);
  }
};

async function sendDailyEvent(event: string, count: number): Promise<boolean> {
  return await sendEvent(event, count);
}

const checkSendLastActive = async (forceRefresh: boolean) => {
  try {
    const lastSend = localStorage.getItem('last_send_last_active');
    const now = new Date();
    const nowTimestamp = now.getTime();
    
    const lastUpdateTimestamp = lastSend ? parseInt(lastSend, 10) : 0;
    const interval = 1 * 60 * 60 * 1000; // 1 hs

    if (nowTimestamp - lastUpdateTimestamp >= interval || forceRefresh) {
      console.log("Sending last active");
      const success = await sendLastActive(0);
      if (success) {
          localStorage.setItem('last_send_last_active', nowTimestamp.toString());
      }
    }
  } catch (error) {
    console.error('Error al actualizar last_active:', error);
  }
};

async function sendLastActive(count: number): Promise<boolean> {
  try {
    const jwt = localStorage.getItem('jwt');
    const res = await axios.post(`https://api.abogalia.com.ar/abogalia/api/v1/users/last-active`, {}, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        Accept: 'application/json',
      },
    });

    if (res.status === 200) {
        return true;
    }
    return false;
  } catch (error: any) {
    if (error.response && error.response.status === 401 && count === 0) {
      console.log("Unauthorized Sending last active: ", error);
      const shouldRetry = await refreshToken(null);
      if (shouldRetry) {
          await sendLastActive(1);
      }
    }
    
    console.log("Error Sending last active: ", error);
    return false;
  }
}

interface UserOnboarding {
  id: number;
  user_id: string;
  key: string;
  expires_at: number | null;
  shown_at: number | null;
  created_at: number;
  updated_at: number;
}

const checkDownloadOnboardings = async (forceRefresh: boolean) => {
  try {
    const lastDownload = localStorage.getItem('last_check_download_onboardings');
    const now = new Date();
    const nowTimestamp = now.getTime();
    
    const lastUpdateTimestamp = lastDownload ? parseInt(lastDownload, 10) : 0;
    const interval = 12 * 60 * 60 * 1000; // 12 hs

    if (nowTimestamp - lastUpdateTimestamp >= interval || forceRefresh) {
      console.log("Downloading onboardings");
      const success = await getOnboardings(0);
      if (success) {
          localStorage.setItem('last_check_download_onboardings', nowTimestamp.toString());
      }
    }
  } catch (error) {
    console.error('Error al descargar onboardings:', error);
  }
};

async function getOnboardings(count: number): Promise<boolean> {
  try {
    const jwt = localStorage.getItem('jwt');
    if (!jwt) {
      return false;
    }

    const res = await axios.get(`https://api.abogalia.com.ar/abogalia/api/v1/users/onboardings`, {
      headers: {
        Authorization: `Bearer ${jwt}`,
        Accept: 'application/json',
      },
    });

    if (res.status === 200 && Array.isArray(res.data)) {
      // Almacenar onboardings en localStorage como un objeto indexado por key
      const onboardingsByKey: Record<string, UserOnboarding> = {};
      res.data.forEach((onboarding: UserOnboarding) => {
        onboardingsByKey[onboarding.key] = onboarding;
      });
      localStorage.setItem("user_onboardings", JSON.stringify(onboardingsByKey));
      return true;
    }
    return false;
  } catch (error: any) {
    if (error.response && error.response.status === 401 && count === 0) {
      console.log("Unauthorized downloading onboardings: ", error);
      const shouldRetry = await refreshToken(null);
      if (shouldRetry) {
        return await getOnboardings(1);
      }
    }
    
    console.log("Error downloading onboardings: ", error);
    return false;
  }
}

const checkPushNotificationPermission = async () => {
  try {
    const storedUser = localStorage.getItem('user');
    const user = storedUser ? JSON.parse(storedUser) : {};
    if (!isLawyer(user)) return;

    if (typeof Notification === 'undefined' || Notification.permission === 'denied') return;

    // Si permission es 'default', el banner PushNotificationBanner se encarga de pedir el permiso.
    // Aquí solo hacemos el re-registro silencioso cuando ya está concedido (rotación de token FCM).
    if (Notification.permission !== 'granted') return;

    const lastCheck = localStorage.getItem('last_check_push_token');
    const now = Date.now();
    const interval = 7 * 24 * 60 * 60 * 1000; // 7 días
    if (lastCheck && now - parseInt(lastCheck, 10) < interval) return;

    const token = await requestPushPermissionAndGetToken();
    if (token) {
      const ok = await registerPushToken(0, token);
      if (ok) {
        localStorage.setItem('last_check_push_token', now.toString());
      }
    }
  } catch (error) {
    console.error('Error checking push notification permission:', error);
  }
};

const useCheckInfo = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  useEffect(() => {
    const runWithDelay = async () => {
      if (location.pathname === "/user/login") {
        return;
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const refresh = queryParams.get("refresh") === "true";
      console.log("useCheckInfo: ", location.pathname);
      fetchData(refresh);
    };
    
    runWithDelay();
  }, [location.pathname]);
};

export { fetchData };
export default useCheckInfo;
